/**
 * Role — the Zod source of types for the ONE roles table.
 *
 * Foundations 1 (Users rail), B1-T1. principles.md #1 ("one store, one
 * resolver, one owner per noun") and #5 ("reference by key, never by name").
 *
 * WHAT CHANGED AND WHY
 *   Before c24 a "role" was three things at once: an enum value on
 *   `user_organizations.role`, a hand-built matrix in
 *   `schemas/permissions.ts` (ROLE_PERMISSIONS), and rows in a `roles` table
 *   whose only key was a display name and whose grants nothing read. c24
 *   makes the `roles` TABLE the single store: the five shipped roles are
 *   platform rows (`organization_id IS NULL`, `is_platform = true`) addressed
 *   by a stable `key`, and a tenant's own roles are rows beside them.
 *
 * SHIPPED ROLES ARE READ-ONLY (founder decision C5, 2026-09-08: "shipped role
 * stays as is — tenant can make more if he needs to"). A tenant clones one to
 * get an editable copy; `basedOnKey` records where the clone came from. There
 * is no per-tenant override copy of a platform role.
 *
 * NOTE FOR B1-T3: `parseRoleGrants` below is the ONE parser for a stored
 * grant array. Do not add a second one in `permissions.ts`; move call sites
 * here instead.
 */

import { z } from 'zod';
import {
  RbacActionSchema,
  RbacResourceSchema,
  permissionKey,
  type RbacPermission,
} from './permissions.js';

/**
 * A role key. Stable, lowercase, machine-addressable — the thing every
 * reference uses (principle 5).
 *
 * The regex admits digits after the first character on purpose. c24 derives a
 * key for every pre-existing tenant role from its display name, and real
 * tenant roles are named things like "Level 2 Approver" → `level_2_approver`.
 * A `^[a-z_]+$` key would have rejected live data at migration time.
 */
export const RoleKeySchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9_]{0,62}$/,
    'A role key starts with a letter and contains only lowercase letters, digits and underscores (max 63 chars)',
  );
export type RoleKey = z.infer<typeof RoleKeySchema>;

/**
 * The five roles the platform ships. Platform data, not tenant data: every
 * tenant sees the same five rows, by the same keys, in every release.
 *
 * `power_user` is a REAL role from c24 forward. It used to be a phantom — a
 * rung in four in-file ladders (connections-module, knowledge, wiki, memory)
 * that no store could ever produce, because `organization_role` had no such
 * value. B1-T2 deletes those ladders; the role survives them.
 */
export const PLATFORM_ROLE_KEYS = [
  'owner',
  'admin',
  'power_user',
  'member',
  'viewer',
] as const;

export const PlatformRoleKeySchema = z.enum(PLATFORM_ROLE_KEYS);
export type PlatformRoleKey = z.infer<typeof PlatformRoleKeySchema>;

export function isPlatformRoleKey(value: unknown): value is PlatformRoleKey {
  return PlatformRoleKeySchema.safeParse(value).success;
}

/**
 * One stored grant. The `roles.permissions` column is a JSONB array of these —
 * the shape the roles route already writes (`[{"action":"write","resource":"agents"}]`),
 * now the shape the column is normalised to and validated against.
 */
export const RoleGrantSchema = z
  .object({
    action: RbacActionSchema,
    resource: RbacResourceSchema,
  })
  .strict();
export type RoleGrant = z.infer<typeof RoleGrantSchema>;

/** A role as the store holds it. */
export const RoleSchema = z.object({
  id: z.string().uuid(),
  key: RoleKeySchema,
  /** NULL = a platform row (the shipped catalogue). Otherwise the tenant. */
  organizationId: z.string().uuid().nullable(),
  isPlatform: z.boolean(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).nullable(),
  permissions: z.array(RoleGrantSchema),
  /** Retired, never deleted, once anything has ever been assigned it. */
  retiredAt: z.string().datetime().nullable(),
  /** Where a retired role's holders get their grants from instead. */
  supersededBy: z.string().uuid().nullable(),
  /** For a tenant role cloned from a platform role: which one (by key). */
  basedOnKey: RoleKeySchema.nullable(),
  /** The release that last upserted a platform row. NULL on tenant rows. */
  platformVersion: z.string().nullable(),
});
export type Role = z.infer<typeof RoleSchema>;

/**
 * Read a stored `permissions` value into permission keys, DROPPING anything
 * that does not parse.
 *
 * principles.md #4 ("nothing fails open"): a grant we cannot understand is a
 * grant we do not honour. A row whose JSONB was hand-edited to
 * `{"action":"*","resource":"*"}`, or that names a resource this release
 * removed, contributes NOTHING — it is never treated as a wildcard and never
 * throws its way past a caller's try/catch into an allow.
 *
 * Returns a de-duplicated array in insertion order. Never throws.
 */
export function parseRoleGrants(value: unknown): RbacPermission[] {
  if (!Array.isArray(value)) return [];
  const out: RbacPermission[] = [];
  const seen = new Set<string>();
  for (const entry of value) {
    const parsed = RoleGrantSchema.safeParse(entry);
    if (!parsed.success) continue;
    const key = permissionKey(parsed.data.action, parsed.data.resource);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/** The inverse: permission keys → the stored `[{action, resource}]` shape. */
export function toRoleGrants(keys: readonly RbacPermission[]): RoleGrant[] {
  const out: RoleGrant[] = [];
  const seen = new Set<string>();
  for (const key of keys) {
    if (seen.has(key)) continue;
    seen.add(key);
    // Actions may contain colons ('role:assign', 'lifecycle:lock'); resources
    // may contain dots but never colons. The LAST colon is the separator.
    const cut = key.lastIndexOf(':');
    if (cut <= 0) continue;
    const parsed = RoleGrantSchema.safeParse({
      action: key.slice(0, cut),
      resource: key.slice(cut + 1),
    });
    if (!parsed.success) continue;
    out.push(parsed.data);
  }
  return out;
}
